import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/tmc/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Filter, BarChart3, ListChecks, ScrollText,
  Settings, Search, Plus, Trash2, X, Loader2, Check, Ban, UserMinus, UserPlus, Users,
  ChevronUp, ChevronDown, Pencil, Save,
} from "lucide-react";

const tmcApi = {
  get: (p, c) => api.get("/tmc" + p, c),
  post: (p, b, c) => api.post("/tmc" + p, b, c),
  put: (p, b, c) => api.put("/tmc" + p, b, c),
  delete: (p, c) => api.delete("/tmc" + p, c),
};
function tmcError(e) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Coś poszło nie tak. Spróbuj ponownie.";
}

const STATUS = {
  pending: { label: "PENDING", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  accepted: { label: "ACCEPTED", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  rejected: { label: "REJECTED", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
  banned: { label: "BANNED", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
};

function StatusBadge({ s }) {
  const c = STATUS[s] || STATUS.pending;
  return <Badge className={`border ${c.cls} font-semibold`} data-testid={`status-${s}`}>{c.label}</Badge>;
}

async function downloadExport(path, filename) {
  const res = await tmcApi.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "members", label: "Członkowie", icon: Users },
  { id: "logs", label: "Logi", icon: ScrollText },
  { id: "settings", label: "Ustawienia", icon: Settings },
];

export default function TmcPanel() {
  const [view, setView] = useState("dashboard");
  const [openApp, setOpenApp] = useState(null);
  const [appsKey, setAppsKey] = useState(0);
  const [questions, setQuestions] = useState([]);

  const loadQuestions = useCallback(async () => {
    try { const { data } = await tmcApi.get("/questions"); setQuestions(data.questions); } catch {}
  }, []);
  useEffect(() => { loadQuestions(); }, [loadQuestions]);
  const closeApp = () => { setOpenApp(null); setAppsKey((k) => k + 1); };

  return (
    <div data-testid="tmc-applications">
      <PageHeader title="Wnioski TMC" subtitle="Weryfikacja i zarządzanie członkami społeczności Discord" />
      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-border pb-3">
        {NAV.map((n) => (
          <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => setView(n.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${view === n.id ? "bg-primary/15 text-white border border-primary/40" : "text-gray-400 hover:text-white hover:bg-secondary/60 border border-transparent"}`}>
            <n.icon className="w-4 h-4" />{n.label}
          </button>
        ))}
      </div>
      {view === "dashboard" && <Dashboard />}
      {view === "members" && <MembersSection />}
      {view === "applications" && <Applications key={appsKey} onOpen={setOpenApp} />}
      {view === "filter" && <FilterView questions={questions} onOpen={setOpenApp} />}
      {view === "analysis" && <AnalysisView questions={questions} onOpen={setOpenApp} />}
      {view === "form" && <FormBuilder questions={questions} reload={loadQuestions} />}
      {view === "logs" && <Logs />}
      {view === "settings" && <SettingsView />}
      {openApp && <AppDialog id={openApp} onClose={closeApp} />}
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-[#12151c] border border-white/5 rounded-2xl ${className}`}>{children}</div>;
}

const MEMBER_STATUS = {
  awaiting_setup: ["Czeka na ustawienie", "text-slate-400"],
  pending_admin: ["Do akceptacji", "text-amber-400"],
  accepted: ["Zaakceptowany", "text-emerald-400"],
  rejected: ["Odrzucony", "text-rose-400"],
};

function MembersSection() {
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState("");
  const load = useCallback(() => {
    tmcApi.get("/members").then((r) => setRows(r.data.members || [])).catch(() => {});
    tmcApi.get("/bot-admins").then((r) => setAdmins(r.data.admins || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (m, kind) => {
    try {
      const r = await tmcApi.post(`/members/${m.telegram_user_id}/${kind}`, { group_chat_id: m.group_chat_id });
      if (kind === "accept") {
        const d = r?.data || {};
        if (!d.ok) toast.warning(`Zaakceptowano, ale błąd linku Discord: ${d.error}`);
        else if (d.dm_sent) toast.success("Zaakceptowano — link wysłany na Telegramie ✅");
        else { if (d.invite) { try { await navigator.clipboard.writeText(d.invite); } catch (e) {} } toast.warning("Zaakceptowano. Osoba nie uruchomiła bota — link skopiowany do schowka, wyślij go ręcznie."); }
      } else toast.success("Odrzucono");
      load();
    } catch (e) { toast.error(tmcError(e)); }
  };
  const removeM = async (m) => {
    if (!window.confirm("Usunąć członka z systemu i wyrzucić z grupy?")) return;
    try { await tmcApi.delete(`/members/${m.telegram_user_id}`); load(); toast.success("Usunięto członka"); } catch (e) { toast.error(tmcError(e)); }
  };
  const clearAll = async () => {
    if (!window.confirm("Usunąć wszystkich członków z tej listy? Nowi będą dodawani automatycznie.")) return;
    try { await tmcApi.delete("/members"); load(); toast.success("Lista wyczyszczona"); } catch (e) { toast.error(tmcError(e)); }
  };
  const addAdmin = async () => {
    if (!newAdmin.trim()) { toast.error("Podaj ID Telegram"); return; }
    try { await tmcApi.post("/bot-admins", { chat_id: newAdmin.trim() }); setNewAdmin(""); load(); toast.success("Dodano administratora"); }
    catch (e) { toast.error(tmcError(e)); }
  };
  const delAdmin = async (cid) => { try { await tmcApi.delete(`/bot-admins/${cid}`); load(); } catch (e) { toast.error(tmcError(e)); } };
  return (
    <div className="space-y-5" data-testid="members-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <H sub="Nowi członkowie z bota Telegram: nick Telegram ↔ nick Discord ↔ ID TradeQuo">Członkowie</H>
        <div className="flex items-center gap-2">
          <Button data-testid="members-refresh-btn" onClick={load} variant="outline" className="border-white/10 text-slate-200">Odśwież</Button>
          <Button data-testid="members-clear-btn" onClick={clearAll} variant="outline" className="border-white/10 text-rose-300"><Trash2 className="w-4 h-4 mr-1" />Wyczyść listę</Button>
        </div>
      </div>

      <Card className="p-5 space-y-3" data-testid="bot-admins-card">
        <div className="font-semibold text-white">Administratorzy bota</div>
        <div className="text-xs text-slate-500">Osoby, które dostają prośby o akceptację i mogą przyjmować/odrzucać członków. Podaj numeryczne ID Telegram (osoba musi najpierw napisać <b>/start</b> do bota, aby mogła otrzymywać powiadomienia).</div>
        <div className="flex gap-2">
          <Input data-testid="admin-id-input" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} placeholder="np. 7736253422" className="bg-[#0a0c10] border-white/10 text-white" />
          <Button data-testid="add-admin-btn" onClick={addAdmin} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"><UserPlus className="w-4 h-4 mr-1" />Dodaj</Button>
        </div>
        <div className="space-y-1">
          {admins.map((a, i) => (
            <div key={i} data-testid={`admin-${a.chat_id}`} className="flex items-center justify-between text-sm border border-white/5 rounded-lg px-3 py-2">
              <span className="text-slate-200 font-mono">{a.chat_id}{a.note ? <span className="text-slate-500 font-sans"> · {a.note}</span> : null}</span>
              <button data-testid={`del-admin-${a.chat_id}`} onClick={() => delAdmin(a.chat_id)} className="text-rose-400 text-xs">Usuń</button>
            </div>
          ))}
          {admins.length === 0 && <div className="text-xs text-slate-500">Brak administratorów. Napisz /start do bota lub dodaj ID wyżej.</div>}
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="members-table">
        <div className="grid grid-cols-[1.4fr_1.4fr_.8fr_1fr_1.1fr_1.2fr] text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 border-b border-white/5">
          <div>Telegram</div><div>Discord</div><div>ID TradeQuo</div><div>Status</div><div>Data</div><div>Akcje</div>
        </div>
        {rows.map((m, i) => {
          const st = MEMBER_STATUS[m.status] || [m.status || "—", "text-slate-400"];
          return (
            <div key={i} data-testid={`member-row-${i}`} className="grid grid-cols-[1.4fr_1.4fr_.8fr_1fr_1.1fr_1.2fr] px-4 py-2.5 border-b border-white/5 text-sm items-center">
              <div className="min-w-0 truncate text-white">{m.display_name || "—"}{m.telegram_username ? <span className="text-slate-500"> @{m.telegram_username}</span> : null}</div>
              <div className="min-w-0 truncate text-slate-200">{m.discord_name || "—"}</div>
              <div className="text-white font-mono">{m.tradequo_id || "—"}</div>
              <div className={st[1]}>{st[0]}</div>
              <div className="text-slate-500 text-xs">{m.joined_at ? new Date(m.joined_at).toLocaleString() : (m.requested_at ? new Date(m.requested_at).toLocaleString() : "")}</div>
              <div className="flex gap-1.5 flex-wrap items-center">
                <button data-testid={`accept-${i}`} onClick={() => act(m, "accept")} className="text-emerald-400 text-xs border border-emerald-500/30 rounded px-2 py-1 hover:bg-emerald-500/10">Akceptuj</button>
                <button data-testid={`reject-${i}`} onClick={() => act(m, "reject")} className="text-rose-400 text-xs border border-rose-500/30 rounded px-2 py-1 hover:bg-rose-500/10">Odrzuć</button>
                {m.discord_invite && <button data-testid={`copy-dc-${i}`} onClick={() => { navigator.clipboard.writeText(m.discord_invite); toast.success("Skopiowano link DC"); }} className="text-emerald-300 text-xs underline px-1">Link DC</button>}
                <button data-testid={`del-${i}`} onClick={() => removeM(m)} className="text-slate-400 text-xs border border-white/10 rounded px-2 py-1 hover:bg-white/5">Usuń</button>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <div className="px-4 py-10 text-center text-slate-500 text-sm" data-testid="members-empty">Brak członków. Pojawią się tu automatycznie, gdy ktoś dołączy przez bota Telegram.</div>}
      </Card>
    </div>
  );
}
function H({ children, sub }) {
  return <div className="mb-6"><h1 className="text-2xl font-bold text-white">{children}</h1>{sub && <p className="text-slate-500 text-sm mt-1">{sub}</p>}</div>;
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { tmcApi.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  const cards = [
    { k: "unique_members", label: "Członkowie", cls: "text-white" },
    { k: "total", label: "Wnioski łącznie", cls: "text-sky-400" },
    { k: "pending", label: "Oczekujące", cls: "text-amber-400" },
    { k: "accepted", label: "Przyjęci", cls: "text-emerald-400" },
    { k: "rejected", label: "Odrzuceni", cls: "text-rose-400" },
    { k: "banned", label: "Zbanowani", cls: "text-slate-300" },
  ];
  return (
    <div>
      <H sub="Przegląd społeczności TMC">Dashboard</H>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-stats">
        {cards.map((c) => (
          <Card key={c.k} className="p-5">
            <div className="text-slate-500 text-sm">{c.label}</div>
            <div className={`text-3xl font-bold mt-2 ${c.cls}`}>{d ? d[c.k] : "—"}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AppRow({ a, onOpen }) {
  return (
    <button data-testid={`app-row-${a.discord_user_id}`} onClick={() => onOpen(a.id)}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-left border-b border-white/5 last:border-0">
      <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-sm shrink-0">
        {(a.discord_display_name || a.discord_username || "?")[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{a.discord_nickname || a.discord_display_name || a.discord_username}</div>
        <div className="text-xs text-slate-500 truncate">@{a.discord_username} · {new Date(a.submitted_at).toLocaleDateString("pl-PL")}{a.version > 1 ? ` · wniosek #${a.version}` : ""}</div>
      </div>
      <StatusBadge s={a.status} />
    </button>
  );
}

function Applications({ onOpen }) {
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");
  const [apps, setApps] = useState([]);
  const load = useCallback(async () => {
    const { data } = await tmcApi.get("/applications", { params: { status: tab, search } });
    setApps(data.applications);
  }, [tab, search]);
  useEffect(() => { load(); }, [load]);
  const tabs = [["", "Wszystkie"], ["pending", "Oczekujące"], ["accepted", "Przyjęte"], ["rejected", "Odrzucone"], ["banned", "Zbanowane"]];
  return (
    <div>
      <H sub="Wnioski o dołączenie z Discorda">Wnioski</H>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {tabs.map(([v, l]) => (
          <button key={v} data-testid={`tab-${v || "all"}`} onClick={() => setTab(v)}
            className={`px-3 py-1.5 rounded-lg text-sm ${tab === v ? "bg-emerald-500/10 text-emerald-400" : "text-slate-400 hover:bg-white/5"}`}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="app-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj: nick, Discord ID, odpowiedź..." className="bg-[#12151c] border-white/10 text-white pl-9 w-64" />
        </div>
        <Button size="sm" variant="outline" data-testid="export-xlsx"
          onClick={() => downloadExport("/export/applications.xlsx", "tmc_wnioski.xlsx")}
          className="border-emerald-500/30 text-emerald-400">Excel</Button>
        <Button size="sm" variant="outline" data-testid="export-csv"
          onClick={() => downloadExport("/export/applications.csv", "tmc_wnioski.csv")}
          className="border-white/10 text-slate-300">CSV</Button>
      </div>
      <Card>
        {apps.length === 0 ? <div className="p-8 text-center text-slate-500">Brak wniosków</div>
          : apps.map((a) => <AppRow key={a.id} a={a} onOpen={onOpen} />)}
      </Card>
    </div>
  );
}

function QuestionPicker({ questions, value, onChange }) {
  return (
    <select data-testid="question-select" value={value} onChange={(e) => onChange(e.target.value)}
      className="bg-[#0a0c10] border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-full">
      <option value="">— wybierz pytanie —</option>
      {questions.map((q) => <option key={q.id} value={q.id}>{q.text}{!q.active ? " (usunięte)" : ""}</option>)}
    </select>
  );
}

function AnswerInput({ q, value, onChange, testid }) {
  if (q && (q.type === "select" || q.type === "multiselect") && q.choices?.length) {
    return (
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-[#0a0c10] border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-full">
        <option value="">— wybierz odpowiedź —</option>
        {q.choices.map((c) => <option key={c.id} value={c.label}>{c.label}</option>)}
      </select>
    );
  }
  return <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    placeholder="Wpisz dokładną odpowiedź" className="bg-[#0a0c10] border-white/10 text-white" />;
}

function FilterView({ questions, onOpen }) {
  const [filters, setFilters] = useState([{ question_id: "", value: "" }]);
  const [res, setRes] = useState(null);
  const setF = (i, patch) => setFilters(filters.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const run = async () => {
    const active = filters.filter((f) => f.question_id && f.value);
    const { data } = await tmcApi.post("/applications/filter", { filters: active });
    setRes(data);
  };
  return (
    <div>
      <H sub="Wybierz pytanie i odpowiedź — pokaże wszystkich, którzy tak odpowiedzieli">Filtruj po odpowiedzi</H>
      <Card className="p-5 space-y-3">
        {filters.map((f, i) => {
          const q = questions.find((x) => x.id === f.question_id);
          return (
            <div key={i} className="grid md:grid-cols-2 gap-3 items-center">
              <QuestionPicker questions={questions} value={f.question_id} onChange={(v) => setF(i, { question_id: v, value: "" })} />
              <div className="flex gap-2">
                <div className="flex-1"><AnswerInput q={q} value={f.value} onChange={(v) => setF(i, { value: v })} testid={`filter-answer-${i}`} /></div>
                {filters.length > 1 && <Button variant="ghost" size="icon" onClick={() => setFilters(filters.filter((_, idx) => idx !== i))}><X className="w-4 h-4 text-slate-500" /></Button>}
              </div>
            </div>
          );
        })}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" data-testid="add-filter-btn" onClick={() => setFilters([...filters, { question_id: "", value: "" }])}
            className="border-white/10 text-slate-300"><Plus className="w-4 h-4 mr-1" />Dodaj filtr</Button>
          <Button data-testid="run-filter-btn" onClick={run} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Szukaj</Button>
        </div>
      </Card>
      {res && (
        <div className="mt-5">
          <div className="flex gap-4 text-sm mb-3" data-testid="filter-stats">
            <span className="text-white font-semibold">Łącznie: {res.total}</span>
            <span className="text-emerald-400">Przyjęci: {res.stats.accepted}</span>
            <span className="text-amber-400">Oczekujący: {res.stats.pending}</span>
            <span className="text-rose-400">Odrzuceni: {res.stats.rejected}</span>
          </div>
          <Card>{res.applications.length === 0 ? <div className="p-8 text-center text-slate-500">Brak wyników</div>
            : res.applications.map((a) => <AppRow key={a.id} a={a} onOpen={onOpen} />)}</Card>
        </div>
      )}
    </div>
  );
}

function AnalysisView({ questions, onOpen }) {
  const [qid, setQid] = useState("");
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  useEffect(() => { setData(null); setDrill(null); if (qid) tmcApi.get(`/analysis/${qid}`).then((r) => setData(r.data)).catch(() => {}); }, [qid]);
  const drillDown = async (answer) => {
    const { data: r } = await tmcApi.post("/applications/filter", { filters: [{ question_id: qid, value: answer }] });
    setDrill({ answer, ...r });
  };
  const max = data?.results?.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <div>
      <H sub="Statystyki odpowiedzi na wybrane pytanie">Analiza odpowiedzi</H>
      <Card className="p-5"><QuestionPicker questions={questions} value={qid} onChange={setQid} /></Card>
      {data && (
        <Card className="p-5 mt-4 space-y-2" data-testid="analysis-results">
          {data.results.length === 0 ? <div className="text-slate-500 text-sm">Brak danych</div> : data.results.map((r) => (
            <button key={r.answer} onClick={() => drillDown(r.answer)} className="w-full text-left group">
              <div className="flex justify-between text-sm mb-1"><span className="text-slate-200 group-hover:text-emerald-400">{r.answer}</span><span className="text-slate-400 font-semibold">{r.count}</span></div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-emerald-500/70" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </button>
          ))}
        </Card>
      )}
      {drill && (
        <div className="mt-5">
          <div className="text-sm text-slate-400 mb-3">Użytkownicy z odpowiedzią: <span className="text-emerald-400 font-semibold">{drill.answer}</span> ({drill.total})</div>
          <Card>{drill.applications.map((a) => <AppRow key={a.id} a={a} onOpen={onOpen} />)}</Card>
        </div>
      )}
    </div>
  );
}

function FormBuilder({ questions, reload }) {
  const [text, setText] = useState("");
  const [type, setType] = useState("text");
  const [choices, setChoices] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editChoices, setEditChoices] = useState("");
  const active = questions.filter((q) => q.active);
  const add = async () => {
    if (!text.trim()) return;
    const body = { text, type, choices: ["select", "multiselect"].includes(type) ? choices.split("\n").map((l) => ({ label: l.trim() })).filter((c) => c.label) : [] };
    try { await tmcApi.post("/questions", body); setText(""); setChoices(""); setType("text"); reload(); toast.success("Dodano"); }
    catch (e) { toast.error(tmcError(e)); }
  };
  const del = async (id) => { await tmcApi.delete(`/questions/${id}`); reload(); toast.success("Pytanie usunięte (odpowiedzi zachowane)"); };
  const startEdit = (q) => { setEditId(q.id); setEditText(q.text); setEditChoices((q.choices || []).map((c) => c.label).join("\n")); };
  const saveEdit = async (q) => {
    const body = { text: editText };
    if (["select", "multiselect"].includes(q.type)) body.choices = editChoices.split("\n").map((l) => ({ label: l.trim() })).filter((c) => c.label);
    try { await tmcApi.put(`/questions/${q.id}`, body); setEditId(null); reload(); toast.success("Zapisano pytanie"); }
    catch (e) { toast.error(tmcError(e)); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= active.length) return;
    const a = active[i], b = active[j];
    try { await tmcApi.put(`/questions/${a.id}`, { order: b.order }); await tmcApi.put(`/questions/${b.id}`, { order: a.order }); reload(); }
    catch (e) { toast.error(tmcError(e)); }
  };
  return (
    <div>
      <H sub="Dodawaj, edytuj i przesuwaj pytania — historyczne odpowiedzi zostają nienaruszone">Formularz</H>
      <Card className="p-5 mb-5 space-y-3">
        <Textarea data-testid="q-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Treść pytania — albo gotowa wiadomość/tekst do wyświetlenia (możesz wkleić wiele linii)" className="bg-[#0a0c10] border-white/10 text-white" rows={3} />
        <div className="flex gap-3 flex-wrap">
          <select data-testid="q-type-select" value={type} onChange={(e) => setType(e.target.value)} className="bg-[#0a0c10] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="text">Tekst</option><option value="select">Lista wyboru (1)</option><option value="multiselect">Wielokrotny wybór</option><option value="info">Informacja (bez odpowiedzi)</option>
          </select>
        </div>
        {["select", "multiselect"].includes(type) && <Textarea data-testid="q-choices-input" value={choices} onChange={(e) => setChoices(e.target.value)} placeholder="Opcje — każda w nowej linii" className="bg-[#0a0c10] border-white/10 text-white" rows={4} />}
        <Button data-testid="add-question-btn" onClick={add} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Dodaj pytanie</Button>
      </Card>
      <div className="space-y-2">
        {active.map((q, i) => (
          <Card key={q.id} className="p-4" data-testid={`q-row-${q.id}`}>
            {editId === q.id ? (
              <div className="space-y-2">
                <Textarea data-testid={`edit-q-text-${q.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} className="bg-[#0a0c10] border-white/10 text-white" rows={3} />
                {["select", "multiselect"].includes(q.type) && <Textarea value={editChoices} onChange={(e) => setEditChoices(e.target.value)} rows={3} className="bg-[#0a0c10] border-white/10 text-white" placeholder="Opcje — każda w nowej linii" />}
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`save-q-${q.id}`} onClick={() => saveEdit(q)} className="bg-emerald-500 hover:bg-emerald-400 text-black"><Save className="w-4 h-4 mr-1" />Zapisz</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>Anuluj</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <button data-testid={`up-q-${q.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-400 hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button data-testid={`down-q-${q.id}`} disabled={i === active.length - 1} onClick={() => move(i, 1)} className="text-slate-400 hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <span className="text-xs text-slate-600 font-mono w-6 text-center">{i + 1}</span>
                <div className="flex-1"><div className="text-white text-sm whitespace-pre-wrap">{q.text}</div><div className="text-xs text-slate-500">{q.type === "info" ? "informacja (bez odpowiedzi)" : q.type}{q.choices?.length ? ` · ${q.choices.map((c) => c.label).join(", ")}` : ""}</div></div>
                <Button variant="ghost" size="icon" data-testid={`edit-q-${q.id}`} onClick={() => startEdit(q)}><Pencil className="w-4 h-4 text-sky-400" /></Button>
                <Button variant="ghost" size="icon" data-testid={`del-q-${q.id}`} onClick={() => del(q.id)}><Trash2 className="w-4 h-4 text-rose-400" /></Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}


function Logs() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { tmcApi.get("/logs").then((r) => setLogs(r.data.logs)).catch(() => {}); }, []);
  return (
    <div>
      <H sub="Historia akcji administratorów">Logi</H>
      <Card>
        {logs.length === 0 ? <div className="p-8 text-center text-slate-500">Brak logów</div> : logs.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 border-b border-white/5 last:border-0 text-sm">
            <span className="text-slate-500 font-mono text-xs">{new Date(l.created_at).toLocaleString("pl-PL")}</span>
            <span className="text-emerald-400 font-semibold">{l.action}</span>
            <span className="text-slate-300">{l.admin_name}</span>
            <span className="text-slate-500">→ {l.discord_user_id}</span>
            <span className={`ml-auto text-xs ${l.result === "SUCCESS" ? "text-emerald-400" : "text-amber-400"}`}>{l.result}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function SettingsView() {
  const [s, setS] = useState(null);
  useEffect(() => { tmcApi.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await tmcApi.put("/settings", s); setS(data); toast.success("Zapisano"); } catch (e) { toast.error(tmcError(e)); }
  };
  if (!s) return null;
  const F = ({ label, k, val }) => (
    <div><label className="text-slate-300 text-sm">{label}</label>
      <Input data-testid={`set-${k}`} value={val ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="bg-[#0a0c10] border-white/10 text-white mt-1" /></div>
  );
  return (
    <div className="space-y-6">
      <H sub="Discord: jednorazowy link do dołączenia wysyłany po akceptacji członka">Ustawienia</H>
      <Card className="p-5 space-y-4 max-w-lg">
        <div className="font-semibold text-white">Discord — zaproszenia</div>
        <F label="Guild ID (serwer)" k="guild_id" val={s.guild_id} />
        <F label="Kanał do zaproszeń (ID) — tu tworzony jest jednorazowy link" k="invite_channel_id" val={s.invite_channel_id} />
        <Button data-testid="save-settings-btn" onClick={save} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Zapisz</Button>
      </Card>

      <InvitesSection s={s} setS={setS} F={F} save={save} />
    </div>
  );
}

function InvitesSection({ s, setS, F, save }) {
  const [invites, setInvites] = useState([]);
  const [tqid, setTqid] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    tmcApi.get("/discord-invites").then((r) => setInvites(r.data.invites)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const gen = async () => {
    if (!tqid.trim()) { toast.error("Podaj ID TradeQuo"); return; }
    setBusy(true);
    try { await tmcApi.post("/discord-invites", { tradequo_id: tqid, name }); setTqid(""); setName(""); load(); toast.success("Link wygenerowany ✅"); }
    catch (e) { toast.error(tmcError(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { await tmcApi.delete(`/discord-invites/${id}`); load(); };
  return (
    <Card className="p-5 space-y-4 max-w-2xl" data-testid="invites-section">
      <div className="font-semibold text-white">Linki zapraszające TradeQuo → Discord</div>
      <div className="text-xs text-slate-500">Link jest <b>jednorazowy</b>; po dołączeniu osoba dostaje rolę VIP, a Ty widzisz ją niżej (ID TradeQuo ↔ nick Discord ↔ data). Bot sam wybiera kanał na serwerze — ustaw tylko Guild ID (w sekcji Ustawienia wyżej) i rolę VIP.</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Rola VIP member (ID)" k="vip_role_id" val={s.vip_role_id} />
      </div>
      <Button data-testid="save-invite-settings" onClick={save} variant="outline" className="border-white/10 text-slate-200">Zapisz ustawienia linków</Button>
      <div className="border-t border-white/5 pt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div><label className="text-slate-300 text-sm">ID TradeQuo</label>
          <Input data-testid="invite-tqid" value={tqid} onChange={(e) => setTqid(e.target.value)} className="bg-[#0a0c10] border-white/10 text-white mt-1" /></div>
        <div><label className="text-slate-300 text-sm">Imię / notatka (opcjonalnie)</label>
          <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0a0c10] border-white/10 text-white mt-1" /></div>
        <Button data-testid="gen-invite-btn" disabled={busy} onClick={gen} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Generuj link</Button>
      </div>
      <div className="space-y-2">
        {invites.map((iv) => (
          <div key={iv.id} className="flex items-center justify-between gap-2 border border-white/5 rounded-lg p-2.5 text-sm" data-testid={`invite-${iv.id}`}>
            <div className="min-w-0"><div className="text-white truncate">TradeQuo: <b>{iv.tradequo_id}</b>{iv.name ? ` · ${iv.name}` : ""}</div>
              <a href={iv.url} target="_blank" rel="noreferrer" className="text-emerald-400 text-xs truncate">{iv.url}</a></div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => { navigator.clipboard.writeText(iv.url); toast.success("Skopiowano"); }} className="text-slate-300 text-xs underline">Kopiuj</button>
              <button onClick={() => del(iv.id)} className="text-rose-400 text-xs">Usuń</button>
            </div>
          </div>
        ))}
        {invites.length === 0 && <div className="text-xs text-slate-500">Brak wygenerowanych linków.</div>}
      </div>
    </Card>
  );
}

function AppDialog({ id, onClose }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [roleId, setRoleId] = useState("");
  const load = useCallback(() => tmcApi.get(`/applications/${id}`).then((r) => setD(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, label) => {
    setBusy(true);
    try { const r = await fn(); toast[r?.data?.ok === false ? "warning" : "success"](label + (r?.data?.discord?.error ? ` — Discord: ${r.data.discord.error}` : "")); load(); }
    catch (e) { toast.error(tmcError(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Usunąć ten wniosek na stałe? Tej operacji nie można cofnąć.")) return;
    setBusy(true);
    try { await tmcApi.delete(`/applications/${id}`); toast.success("Wniosek usunięty"); onClose(); }
    catch (e) { toast.error(tmcError(e)); setBusy(false); }
  };
  const a = d?.application;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#12151c] border-white/10 text-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white">Wniosek użytkownika</DialogTitle>
          <DialogDescription className="text-slate-500">Odpowiedzi z formularza Discord oraz akcje na członku.</DialogDescription>
        </DialogHeader>
        {!a ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div> : (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-lg">{(a.discord_display_name || a.discord_username || "?")[0]?.toUpperCase()}</div>
              <div className="flex-1"><div className="text-white font-semibold">{a.discord_nickname || a.discord_display_name}</div><div className="text-xs text-slate-500">@{a.discord_username} · ID: {a.discord_user_id}</div></div>
              <StatusBadge s={a.status} />
            </div>

            <div>
              <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Odpowiedzi</div>
              <div className="space-y-3" data-testid="app-answers">
                {a.answers.map((ans, i) => (
                  <div key={i}>
                    <div className="text-sm text-slate-400">{ans.question_text}</div>
                    <div className="text-white">→ {ans.value || (ans.values || []).join(", ") || "—"}</div>
                  </div>
                ))}
              </div>
            </div>

            {a.rejection_reason && <div className="text-sm text-rose-400">Powód odrzucenia: {a.rejection_reason}</div>}

            <div className="border-t border-white/5 pt-4">
              <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Zarządzaj członkiem</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" data-testid="accept-btn" disabled={busy} onClick={() => act(() => tmcApi.post(`/applications/${id}/accept`), "Zaakceptowano")} className="bg-emerald-500 hover:bg-emerald-400 text-black"><Check className="w-4 h-4 mr-1" />Akceptuj</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => tmcApi.post(`/applications/${id}/kick`), "Wyrzucono")} className="border-white/10"><UserMinus className="w-4 h-4 mr-1" />Wyrzuć</Button>
                {a.status === "banned"
                  ? <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => tmcApi.post(`/applications/${id}/unban`), "Odbanowano")} className="border-white/10">Odbanuj</Button>
                  : <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => tmcApi.post(`/applications/${id}/ban`, { reason }), "Zbanowano")} className="border-rose-500/30 text-rose-400"><Ban className="w-4 h-4 mr-1" />Ban</Button>}
              </div>
              <div className="flex gap-2 mt-3">
                <Input data-testid="role-id-input" value={roleId} onChange={(e) => setRoleId(e.target.value)} placeholder="Role ID" className="bg-[#0a0c10] border-white/10 text-white h-9" />
                <Button size="sm" variant="outline" disabled={busy || !roleId} onClick={() => act(() => tmcApi.post(`/applications/${id}/role`, { role_id: roleId, action: "add" }), "Rola dodana")} className="border-white/10"><UserPlus className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" disabled={busy || !roleId} onClick={() => act(() => tmcApi.post(`/applications/${id}/role`, { role_id: roleId, action: "remove" }), "Rola usunięta")} className="border-white/10"><UserMinus className="w-4 h-4" /></Button>
              </div>
              <div className="flex gap-2 mt-3">
                <Input data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Powód odrzucenia" className="bg-[#0a0c10] border-white/10 text-white h-9" />
                <Button size="sm" variant="outline" data-testid="reject-btn" disabled={busy} onClick={() => act(() => tmcApi.post(`/applications/${id}/reject`, { reason }), "Odrzucono")} className="border-rose-500/30 text-rose-400"><X className="w-4 h-4 mr-1" />Odrzuć</Button>
              </div>
              <div className="mt-4 pt-3 border-t border-white/5">
                <Button size="sm" variant="outline" data-testid="delete-app-btn" disabled={busy} onClick={del} className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"><Trash2 className="w-4 h-4 mr-1" />Usuń wniosek na stałe</Button>
              </div>
            </div>

            {d.history?.length > 1 && (
              <div className="text-xs text-slate-500">Historia wniosków: {d.history.map((h) => `#${h.version} (${h.status})`).join(" · ")}</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
